/**
 * Pure textarea transforms behind the slide-body formatting toolbar. Each
 * returns the new value plus the selection to restore, so the toolbar
 * buttons stay thin and the behaviour is unit-testable.
 */

export type MarkupAction = 'bold' | 'heading' | 'bullets' | 'numbered' | 'callout';

export interface MarkupResult {
  value: string;
  start: number;
  end: number;
}

const LINE_MARKERS: Record<Exclude<MarkupAction, 'bold'>, RegExp> = {
  heading: /^##\s+/,
  bullets: /^[-*]\s+/,
  numbered: /^\d+[.)]\s+/,
  callout: /^>\s?/,
};

function prefixFor(action: Exclude<MarkupAction, 'bold'>, index: number): string {
  switch (action) {
    case 'heading':
      return '## ';
    case 'bullets':
      return '- ';
    case 'numbered':
      return `${index + 1}. `;
    case 'callout':
      return '> ';
  }
}

export function applyMarkupAction(
  value: string,
  start: number,
  end: number,
  action: MarkupAction,
): MarkupResult {
  if (action === 'bold') {
    const selected = value.slice(start, end);
    const next = `${value.slice(0, start)}**${selected}**${value.slice(end)}`;
    // No selection: park the caret between the asterisks.
    return selected
      ? { value: next, start, end: end + 4 }
      : { value: next, start: start + 2, end: start + 2 };
  }

  // Line actions operate on every whole line the selection touches, and
  // toggle: if all non-empty selected lines already carry the marker it is
  // stripped, otherwise it is added.
  const lineStart = value.lastIndexOf('\n', start - 1) + 1;
  const lineEndIdx = value.indexOf('\n', end);
  const lineEnd = lineEndIdx === -1 ? value.length : lineEndIdx;
  const lines = value.slice(lineStart, lineEnd).split('\n');
  const marker = LINE_MARKERS[action];
  const nonEmpty = lines.filter((l) => l.trim() !== '');
  const allMarked = nonEmpty.length > 0 && nonEmpty.every((l) => marker.test(l.trim()));

  let itemIndex = 0;
  const nextLines = lines.map((line) => {
    if (line.trim() === '') return line;
    const stripped = line.replace(/^\s*/, '');
    if (allMarked) return stripped.replace(marker, '');
    // Strip whichever marker the line already carries so switching list
    // styles replaces it instead of stacking two markers.
    let bare = stripped;
    for (const re of Object.values(LINE_MARKERS)) bare = bare.replace(re, '');
    return prefixFor(action, itemIndex++) + bare;
  });

  const replaced = nextLines.join('\n');
  const next = value.slice(0, lineStart) + replaced + value.slice(lineEnd);
  return { value: next, start: lineStart, end: lineStart + replaced.length };
}
